import React from "react";
//#region types
import { TodoCardProps } from "models/propTypes/todoPropTypes";
import { TranslationKeys } from "translations";
import { User } from "models/propTypes/userProfilePropTypes";
import { ImageMeta } from "models/propTypes/fileViewerPropTypes";
import { FormHandles } from "models/propTypes/genericFormProps";
import { WintSuperkollAPIModel, WintSuperkollAPIModelAnnualAccounts } from "wint-api-client/dist/types";
import { AnnualAccountFormTypes } from "models/formTypes";
//#endregion
//#region components
import TopTodoCard from "../../_partials/topTodoCard";
import {
  useMountedState,
  TitledContainerInner,
  FileViewerContainer,
  Comments,
  EntityCardContent,
  Skeletons,
  TitledContainerBodyText,
  useCheckRelations,
} from "components/common";
import SimpleExpansionPanel from "components/common/panels/simpleExpansionPanel";
import { CommentItem } from "models/propTypes/commentViewPropTypes";
import { Grid, Divider } from "@mui/material";
import { AnnualAccountApproveFormContainer, AnnualAccountFooter, DividendBodyText } from "components/annualAccount";
import { approvalTodoView } from "components/annualAccount/approvalTodoView";
import { useAnnualAccountService, imageTypeToStatementType } from "serviceHooks";
//#endregion
import { DateHelper, LocalizationHelperInstance } from "utils/helpers";
import AuditorSigningInfo from "components/annualAccount/annualReportToApprove/auditorSigningInfo";
import cssExports from "theme/styles/main.module.scss";

const PREVIEW_IMAGE_TYPES = new Set([
  WintSuperkollAPIModel.ImageType.AnnualReport,
  WintSuperkollAPIModel.ImageType.AnnualGeneralMeeting,
]);

const DIVIDEND_DEBOUNCE_MS = 300;

const AnnualAccountToApproveTodo: React.FC<TodoCardProps> = (props) => {
  const annualAccountService = useAnnualAccountService();
  const serviceRef = React.useRef(annualAccountService);
  serviceRef.current = annualAccountService;

  const formRef = React.useRef<FormHandles>(null);
  const fileCache = React.useRef<Map<string, string>>(new Map());
  const debounceTimerRef = React.useRef<ReturnType<typeof setTimeout>>();
  const previewAbortRef = React.useRef<AbortController>();
  const { getMounted } = useMountedState();
  const { getIsRevisor, getIsAuditorAssistant } = useCheckRelations();

  const annualAccountReport = props.item.annualReportForCustomerApproval;
  const financialYear = annualAccountReport?.financialYear
    ? DateHelper.formatFinancialYear(
        annualAccountReport.financialYear.start,
        annualAccountReport.financialYear.end,
        "-"
      )
    : "no financial year found";

  const [attachments, setAttachments] = React.useState<ImageMeta[]>([]);
  const [loadingAttachments, setLoadingAttachments] = React.useState(true);
  const [loadingDividendInformation, setLoadingDividendInformation] = React.useState(false);

  const [dividendInformation, setDividendInformation] =
    React.useState<WintSuperkollAPIModelAnnualAccounts.DividendInformation>();

  const [isAuditCompleted, setIsAuditCompleted] = React.useState(false);
  const { isAuditorView, enableDividend, approveDisabled } = approvalTodoView({
    isRevisor: getIsRevisor(),
    isAuditorAssistant: getIsAuditorAssistant(),
    nonRestrictedEquity: dividendInformation?.annualReportNonRestrictedEquity ?? 0,
    maxAllowedDividend: dividendInformation?.maxAllowedDividend ?? -1,
    isAuditCompleted,
    isCustomerApprovedAtLeastOnce: annualAccountReport?.isCustomerApprovedAtLeastOnce ?? false,
  });

  const reportType = isAuditorView
    ? WintSuperkollAPIModel.FinancialStatementType.AnnualReportAndAnnualGeneralMeeting
    : WintSuperkollAPIModel.FinancialStatementType.AllAnnualDocuments;

  const commentItems = React.useMemo<CommentItem[]>(
    () =>
      [...(annualAccountReport?.comments ?? [])]
        .sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime())
        .map(
          (message): CommentItem => ({
            id: new Date(message.created).getTime(),
            message: message.comment ?? "",
            date: message.created,
            from: new User(message.profileId, message.profileName),
          })
        ),
    [annualAccountReport?.comments]
  );

  // TODO fix a clean way to get the created date - DEV-8008 https://app.clickup.com/t/4523827/DEV-8008
  const createdDate = commentItems[0]?.date ? DateHelper.formatDate(commentItems[0].date) : "";

  React.useEffect(() => {
    setLoadingAttachments(true);
    annualAccountReport &&
      annualAccountService
        .getDocumentsMeta(annualAccountReport.financialYearId, reportType)
        .then((data) => {
          if (getMounted() && data.numberOfAttachments > 0)
            setAttachments(
              data.attachmentsMeta
                ? data.attachmentsMeta.map((meta) => ({
                    id: meta.attachmentId ?? "",
                    contentType: meta.contentType,
                    numberOfPages: meta.numberOfPages,
                    imageType: meta.imageType,
                  }))
                : []
            );
        })
        .catch(() => {
          if (getMounted()) setAttachments([]);
        })
        .finally(() => {
          if (getMounted()) setLoadingAttachments(false);
        });
  }, [annualAccountReport?.id, reportType]);

  React.useEffect(() => {
    if (annualAccountReport) {
      setLoadingDividendInformation(true);
      annualAccountService
        .getDividendInformation(annualAccountReport.financialYearId)
        .then((result) => {
          if (getMounted()) setDividendInformation(result);
        })
        .finally(() => {
          if (getMounted()) setLoadingDividendInformation(false);
        });
    }
  }, [annualAccountReport?.id]);

  const getFile = React.useCallback(
    (_attachmentId: number | string) => {
      const cacheKey = String(_attachmentId);
      const cached = fileCache.current.get(cacheKey);
      if (cached) return Promise.resolve(cached);

      const attachment = attachments.find((a) => String(a.id) === cacheKey);
      if (!annualAccountReport || !attachment) return Promise.resolve("");

      const isPreviewType = attachment.imageType !== undefined && PREVIEW_IMAGE_TYPES.has(attachment.imageType);
      const statementType =
        attachment.imageType !== undefined ? imageTypeToStatementType[attachment.imageType] : undefined;

      const fetchFn =
        isPreviewType && statementType !== undefined
          ? serviceRef.current.getDocumentPreview(annualAccountReport.financialYearId, statementType)
          : serviceRef.current.getDocumentFile(annualAccountReport.financialYearId, attachment.imageType);

      return fetchFn.then((data) => {
        // Skip cache write when a preview refresh is in-flight to avoid overwriting with stale data
        if (data && !previewAbortRef.current) fileCache.current.set(cacheKey, data);
        return data;
      });
    },
    [attachments, annualAccountReport]
  );

  const refreshPreviewAttachments = React.useCallback(
    (dividend?: number) => {
      if (!annualAccountReport) return;

      const previewAttachments = attachments.filter(
        (a) => a.imageType !== undefined && PREVIEW_IMAGE_TYPES.has(a.imageType)
      );
      if (previewAttachments.length === 0) return;

      previewAbortRef.current?.abort();
      const abortController = new AbortController();
      previewAbortRef.current = abortController;

      const promises = previewAttachments.map((attachment) => {
        const statementType = imageTypeToStatementType[attachment.imageType!];
        return serviceRef.current
          .getDocumentPreview(annualAccountReport.financialYearId, statementType, dividend, abortController.signal)
          .then((data: string) => ({
            attachmentId: attachment.id,
            data,
          }));
      });

      Promise.allSettled(promises).then((results) => {
        if (!getMounted() || abortController.signal.aborted) return;
        const fulfilled = results
          .filter(
            (r): r is PromiseFulfilledResult<{ attachmentId: number | string; data: string }> =>
              r.status === "fulfilled"
          )
          .map((r) => r.value);
        if (fulfilled.length === 0) return;
        setAttachments((prev) =>
          prev.map((a) => {
            const result = fulfilled.find((r) => r.attachmentId === a.id);
            if (result) {
              fileCache.current.set(String(a.id), result.data);
              return { ...a, data: result.data };
            }
            return a;
          })
        );
      });
    },
    [annualAccountReport, attachments, getMounted]
  );

  const handleDividendChange = React.useCallback(
    (value: number | undefined, isImmediate: boolean) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      if (isImmediate) {
        refreshPreviewAttachments(value);
      } else {
        debounceTimerRef.current = setTimeout(() => {
          refreshPreviewAttachments(value);
        }, DIVIDEND_DEBOUNCE_MS);
      }
    },
    [refreshPreviewAttachments]
  );

  React.useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      previewAbortRef.current?.abort();
    };
  }, []);

  const fileViewer = React.useMemo(
    () => (
      <FileViewerContainer
        key={annualAccountReport?.id}
        meta={attachments}
        parentId={annualAccountReport?.id ?? 0}
        getFile={getFile}
        loading={annualAccountReport === undefined || loadingAttachments}
      />
    ),
    [attachments, loadingAttachments, annualAccountReport, getFile]
  );

  const submitForm = (submitFunc?: (values: AnnualAccountFormTypes.Approve) => Promise<any>) => {
    return formRef.current?.submit ? formRef.current.submit(submitFunc) : Promise.resolve();
  };

  const getMainSectionHeaderTranslationKey = (): string => {
    if (enableDividend) {
      return TranslationKeys.AnnualAccount.ToApproveTodoMainActionHeaderHasEquity;
    } else if (isAuditorView) {
      return TranslationKeys.AnnualAccount.ToApproveTodoMainActionHeaderIsRevisor;
    }
    return TranslationKeys.AnnualAccount.ToApproveTodoMainActionHeader;
  };

  const getMainSectionSubHeaderTranslationKey = (): string => {
    if (enableDividend) {
      return TranslationKeys.AnnualAccount.ToApproveTodoMainActionDescriptionHasEquity;
    } else if (isAuditorView) {
      return TranslationKeys.AnnualAccount.ToApproveTodoMainActionDescriptionIsRevisor;
    }
    return TranslationKeys.AnnualAccount.ToApproveTodoMainActionDescription;
  };
  const [isK10OrderMode, setIsK10OrderMode] = React.useState(false);
  const hasPartialK10 = dividendInformation?.dividendProposal !== undefined;
  const showK10Toggle =
    enableDividend &&
    dividendInformation?.wintHandlesAllK10 === false &&
    !!dividendInformation?.hasPhysicalOwners &&
    annualAccountReport?.isFirstFinancialYear === false;

  return (
    <EntityCardContent
      topTodoCard={
        <TopTodoCard
          item={props.item}
          overrideTitle={LocalizationHelperInstance.getFormattedMessage(
            enableDividend
              ? TranslationKeys.AnnualAccount.AnnualDocuments
              : TranslationKeys.AnnualAccount.AnnualFinancialStatement
          )}
          descriptionHeadingOne={` ${LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.Common.FinancialYear2
          )} ${financialYear}`}
          descriptionHeadingTwo={annualAccountReport?.companyName}
          footerText={`${LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.Common.Latest
          )}: ${createdDate}`}
        />
      }
      todoMainSection={
        <>
          {loadingDividendInformation ? (
            <Skeletons.TitledContainerSkeleton />
          ) : (
            <TitledContainerInner
              header={LocalizationHelperInstance.getFormattedMessage(getMainSectionHeaderTranslationKey())}
              subHeader={LocalizationHelperInstance.getFormattedMessage(
                getMainSectionSubHeaderTranslationKey(),
                undefined,
                {
                  FinancialYear: `${financialYear}`,
                }
              )}
            >
              {enableDividend && (
                <TitledContainerBodyText>
                  <DividendBodyText />
                </TitledContainerBodyText>
              )}

              {isAuditorView && (
                <AuditorSigningInfo
                  isAuditCompleted={isAuditCompleted}
                  setIsAuditCompleted={setIsAuditCompleted}
                  isCustomerApprovedAtLeastOnce={annualAccountReport?.isCustomerApprovedAtLeastOnce ?? false}
                />
              )}

              {commentItems.length > 0 && (
                <Grid container item marginY={3}>
                  <Comments
                    parentId={`AnnualAccountToApprove-${annualAccountReport?.id ?? 0}`}
                    comments={commentItems}
                    defaultViewCount={2}
                    viewCountIncreaseBy={10}
                    viewCountIncreaseButtonText={LocalizationHelperInstance.getFormattedMessage(
                      TranslationKeys.Common.ViewOlder
                    )}
                  />
                </Grid>
              )}
              {dividendInformation && (
                <>
                  <Divider className={cssExports.dividerGray} />
                  <SimpleExpansionPanel
                    className={`${cssExports.floatSimpleExpansionPanelIconRight} ${cssExports.annualApproveSectionHeader}`}
                    borderless
                    defaultExpanded
                    title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.AnnualAccount.SelectDividend)}
                  >
                    <AnnualAccountApproveFormContainer
                      id={annualAccountReport!.id}
                      financialYearId={annualAccountReport!.financialYearId}
                      ref={formRef}
                      enableDividend={enableDividend}
                      dividendInformation={dividendInformation}
                      isAtCustomerApproval={
                        annualAccountReport?.state ===
                        WintSuperkollAPIModelAnnualAccounts.SuperkollAnnualReportState.AwaitingCustomerApproval
                      }
                      showK10Toggle={showK10Toggle}
                      hasPartialK10={hasPartialK10}
                      isK10OrderMode={showK10Toggle && isK10OrderMode}
                      onK10OrderModeChange={setIsK10OrderMode}
                      onDividendChange={enableDividend ? handleDividendChange : undefined}
                    />
                  </SimpleExpansionPanel>
                  <Divider className={cssExports.dividerGray} />
                  <SimpleExpansionPanel
                    className={`${cssExports.floatSimpleExpansionPanelIconRight} ${cssExports.annualApproveSectionHeader}`}
                    borderless
                    title={LocalizationHelperInstance.getFormattedMessage(
                      TranslationKeys.AnnualAccount.WhatHappensAfterApproval
                    )}
                  >
                    {LocalizationHelperInstance.getFormattedMessage(
                      TranslationKeys.AnnualAccount.WhatHappensAfterApprovalBody
                    )}
                  </SimpleExpansionPanel>
                </>
              )}
            </TitledContainerInner>
          )}
        </>
      }
      footer={
        <AnnualAccountFooter
          annualAccountReport={annualAccountReport!}
          markAsDoneHandler={props.onActionSuccess}
          submitForm={submitForm}
          disableSuccessMessages
          isLoadingDividendInformation={loadingDividendInformation}
          isK10OrderMode={showK10Toggle && isK10OrderMode}
          approveDisabled={approveDisabled}
        />
      }
      infoViewer={fileViewer}
    />
  );
};

export default AnnualAccountToApproveTodo;
